import { createClient } from '@libsql/client';
import { TURSO_URL, getAuthToken } from './encryption';

/**
 * Database connection singleton for Turso
 */
class TursoDatabase {
  constructor() {
    this.client = null;
    this.isConnecting = false;
    this.connectionPromise = null;
    this.password = null;
  }

  /**
   * Set the password for database authentication
   * @param {string} password - The password to use for authentication
   */
  setPassword(password) {
    this.password = password;
    // Clear existing client if password changes
    this.client = null;
  }

  /**
   * Get a database client instance
   * @param {string} [password] - Optional password to use for connection
   * @returns {Promise<Object>} Database client
   */
  async getClient(password) {
    // Use new password if provided, otherwise use stored password
    if (password) {
      this.password = password;
    }

    // Make sure we have a password
    if (!this.password) {
      throw new Error('No password provided for database connection');
    }

    // Return existing client if available
    if (this.client) {
      return this.client;
    }

    // Return existing connection promise if one is in progress
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Start a new connection
    this.isConnecting = true;
    this.connectionPromise = this._connect(this.password);

    try {
      this.client = await this.connectionPromise;
      return this.client;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  /**
   * Connect to the Turso database
   * @param {string} password - The password to use for authentication
   * @returns {Promise<Object>} Database client
   * @private
   */
  async _connect(password) {
    try {
      const authToken = await getAuthToken(password);

      const client = createClient({
        url: TURSO_URL,
        authToken
      });

      console.log('Connected to Turso database');
      return client;
    } catch (error) {
      console.error('Failed to connect to database:', error);
      throw new Error(`Database connection failed: ${error.message}`);
    }
  }

  /**
   * Execute a SQL query
   * @param {string} sql - SQL query to execute
   * @param {Array} [params=[]] - Query parameters
   * @returns {Promise<Object>} Query result
   */
  async query(sql, params = []) {
    const client = await this.getClient();
    try {
      const result = await client.execute({ sql, args: params });
      return result;
    } catch (error) {
      console.error('Query error:', error);
      throw error;
    }
  }

  /**
   * Get all tables in the database
   * @returns {Promise<Array>} List of table names
   */
  async getTables() {
    const result = await this.query("SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'");
    return result.rows.map(row => row.name);
  }

  /**
   * Get table structure
   * @param {string} tableName - Name of the table
   * @returns {Promise<Array>} Table structure information
   */
  async getTableStructure(tableName) {
    const result = await this.query(`PRAGMA table_info(${tableName})`);
    return result.rows;
  }

  /**
   * Get all data from a table
   * @param {string} tableName - Name of the table
   * @param {number} [limit=100] - Maximum number of rows to return
   * @returns {Promise<Array>} Table data
   */
  async getTableData(tableName, limit = 100) {
    const result = await this.query(`SELECT * FROM ${tableName} LIMIT ${limit}`);
    return result.rows;
  }
}

// Create singleton instance
const db = new TursoDatabase();

export default db;